import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  getCurrencies,
  getGames,
  getOperators,
  getPartners,
  getRounds,
  getUser,
  getUserPermissions,
  getPromos,
  getPromoCampaign,
} from './api';

dayjs.extend(utc);

const queryKeys = {
  currencies: ['currencies'] as const,
  user: ['user'] as const,
  partners: ['partners'] as const,
  operators: ['operators'] as const,
  userPermissions: ['userPermissions'] as const,
  games: ['games'] as const,
  rounds: (params: API.TransactionsReq | null) => ['rounds', params] as const,
  promos: (params: {
    partnerName?: string;
    excludeDeleted?: boolean;
    limit: number;
    offset: number;
  }) => ['promos', params] as const,
  promo: (id: string) => ['promo', id] as const,
};

export const useCurrencies = () =>
  useQuery({
    queryKey: queryKeys.currencies,
    queryFn: () => getCurrencies(),
    select: (res) =>
      [...res].sort((a, b) => a.code.localeCompare(b.code, undefined, { sensitivity: 'base' })),
  });

export const useUser = () =>
  useQuery({
    queryKey: queryKeys.user,
    queryFn: () => getUser(),
  });

export const useUserType = () =>
  useQuery({
    queryKey: queryKeys.user,
    queryFn: () => getUser(),
    select: (user) => user.user_type,
  });

export const usePartners = () =>
  useQuery({
    queryKey: queryKeys.partners,
    queryFn: () => getPartners(),
    select: (res) => ({
      partners: [...res.partners].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    }),
  });

export const useOperators = () =>
  useQuery({
    queryKey: queryKeys.operators,
    queryFn: () => getOperators(),
    select: (res) => ({
      operators: [...res.operators].sort((a, b) =>
        a.operatorName.localeCompare(b.operatorName, undefined, { sensitivity: 'base' }),
      ),
    }),
  });

export const useUserPermissions = () =>
  useQuery({
    queryKey: queryKeys.userPermissions,
    queryFn: () => getUserPermissions().then((res) => res.permissions),
  });

export const useGames = () =>
  useQuery({
    queryKey: queryKeys.games,
    queryFn: () => getGames(),
    select: (res) => ({
      games: [...res.games].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    }),
  });

export const usePromos = (params: {
  partnerName?: string;
  excludeDeleted?: boolean;
  limit: number;
  offset: number;
}) =>
  useQuery({
    queryKey: queryKeys.promos(params),
    queryFn: async () =>
      getPromos({
        partnerName: params.partnerName?.trim() || undefined,
        excludeDeleted: params.excludeDeleted,
        limit: params.limit,
        offset: params.offset,
      }),
  });

export const usePromoCampaign = (id: string | null) =>
  useQuery({
    queryKey: queryKeys.promo(id ?? ''),
    queryFn: () => getPromoCampaign(id!),
    enabled: !!id,
  });

export const useRounds = (params: API.TransactionsReq | null) =>
  useQuery({
    queryKey: queryKeys.rounds(params),
    queryFn: async () => {
      const response = await getRounds(params!);
      const fetchedRounds = response.rounds;
      return fetchedRounds.map((round) => ({
        username: round.username,
        gameName: round.gameName,
        betId: round.betId,
        operator: round.operator,
        status: round.status,
        betAmount: round.betAmount,
        winAmount: round.winAmount,
        currency: round.currency,
        convertedBet: round.convertedBet,
        convertedWin: round.convertedWin,
        convertedCurrency: round.convertedCurrency,
        createdAt: dayjs(round.createdAt).utc().format('YYYY-MM-DD HH:mm:ss'),
        multiplier: (round.multiplier / 100.0).toFixed(2),
        tripID: round.tripID,
        crashPoint: (round.crashPoint / 100.0).toFixed(2),
        errorCode: round.errorCode,
        errorText: round.errorText,
      }));
    },
    enabled: !!params,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });
